/*
 * The drive in the quick switcher (Ctrl/Cmd+K): the reader's spaces and the items this visit has read or
 * heard of, found by name, each leading to its address. Their rows stand under the drive's own heading,
 * because a folder or a document is neither a conversation nor a person.
 *
 * The provider answers from the module's slice and never from the network: the switcher asks on every
 * keystroke, and a provider that went to the server would spend a request per letter. A document the
 * reader has not met in this visit is found by Search, which reads the server's index.
 *
 * This file is reached from the entry chunk through the module object: it holds no screen.
 */

import type { QuickSwitcherProvider } from "../../core/modules/registry.ts"
import { scoreTarget } from "../../core/shell/index.ts"
import { client } from "../../core/store/store.ts"
import { spaceName } from "./names.ts"
import { drivePath } from "./navigation.ts"
import { driveSlice } from "./reducer.ts"
import { KindDocument, KindFolder, type ItemView, type SpaceView } from "./wire/drive.gen.ts"

/** How many of this module's rows one query offers. */
const MAX_ROWS = 20

/** The heading this module's rows stand under, as a key of its own catalogue. */
const GROUP = "drive.title"

/** The glyph of an item's row, by its kind. */
function iconOf(item: ItemView): string {
	if (item.kind === KindFolder) return "folder"
	if (item.kind === KindDocument) return "document"
	return "file"
}

/** One candidate row before it is scored. */
interface Candidate {
	id: string
	label: string
	detail?: string
	to: string
	icon: string
	changedAt: number
}

/** The rows the slice can offer: every space, then every live item, each with its space's name. */
function candidates(
	spaces: Record<number, SpaceView>,
	items: Record<number, ItemView>,
): Candidate[] {
	const rows: Candidate[] = Object.values(spaces).map((space) => ({
		id: `drive.space.${space.key}`,
		label: spaceName(space),
		to: drivePath(space.key),
		icon: "folder",
		changedAt: 0,
	}))
	for (const item of Object.values(items)) {
		if (item.trashed !== undefined || item.purged === true) continue
		const space = spaces[item.spaceId]
		rows.push({
			id: `drive.item.${item.key}`,
			label: item.name,
			...(space === undefined ? {} : { detail: spaceName(space) }),
			to: drivePath(item.key),
			icon: iconOf(item),
			changedAt: item.changedAt,
		})
	}
	return rows
}

/**
 * The headings of the documents this visit knows, for a term of two words or more (S-09 §3.3): the
 * rows and their request are a chunk (`search/headings.ts`), fetched with the first such term, so the
 * entry holds no HTTP call of the drive's.
 */
export const driveHeadingSwitcher: QuickSwitcherProvider = {
	id: "drive.headings",
	async query(term: string) {
		if (term.trim().split(/\s+/).length < 2) return []
		return (await import("./search/headings.ts")).headingRows(term)
	},
}

export const driveQuickSwitcher: QuickSwitcherProvider = {
	id: "drive",
	query(term: string) {
		const needle = term.trim().toLocaleLowerCase("en-GB")
		const slice = driveSlice(client.model)
		const found = candidates(slice.spaces, slice.items)
			.map((row) => ({ row, score: scoreTarget(needle, row.label) }))
			.filter((hit): hit is { row: Candidate; score: number } => hit.score !== null)
			.sort((a, b) => b.score - a.score || b.row.changedAt - a.row.changedAt)
			.slice(0, MAX_ROWS)
			.map(({ row }) => ({
				id: row.id,
				label: row.label,
				...(row.detail === undefined ? {} : { detail: row.detail }),
				to: row.to,
				icon: row.icon,
				groupTitleKey: GROUP,
			}))
		return Promise.resolve(found)
	},
}
